import { useState } from 'react'
import { api, ApiError, type IncidentDetail, type ListItem } from '../lib/api'
import { findNatures } from '../lib/natures'

/** THE NATURES OF AN OPEN CALL, AND ANOTHER ONE PUT ON IT (076).
 *
 *  The customer: "he needs a boost and ... he cannot unlock the car because
 *  it's keyless entry ... it will go the nature car lockout with boost
 *  together." The member gets there and finds the car locked too, so the
 *  open call takes "+ Add another nature" as the Take a call form does. Its
 *  questions join the call's, the call code gains its part (BST+LO), and the
 *  thread says who added it. One taken off is MARKED on the server, never
 *  deleted, and its answers stay on the ticket; the first nature stays.
 */
export function CallNatures({
  call, natures, onChanged,
}: {
  call: IncidentDetail
  natures: ListItem[]
  /** The call is read again: its label, code and questions all moved. */
  onChanged: () => Promise<void> | void
}) {
  const [adding, setAdding] = useState(false)
  const [filter, setFilter] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const on = call.natures?.length ? call.natures : null
  const ids = new Set((on ?? []).map((n) => n.nature_id).concat(call.nature_id ?? null))
  const offered = findNatures(natures.filter((n) => !n.retired_at && !ids.has(n.id)), filter)
    .slice(0, 8)

  async function change(fn: () => Promise<unknown>) {
    setBusy(true)
    setError(null)
    try {
      await fn()
      setAdding(false)
      setFilter('')
      await onChanged()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That did not save.')
    } finally {
      setBusy(false)
    }
  }

  // A nature typed rather than chosen has no list to add from, and a closed
  // call keeps its natures as they were.
  if (!call.nature_id || call.status === 'closed') return null

  return (
    <div className="cnat">
      <span className="lbl">Natures</span>
      {(on ?? [{ position: 0, nature_id: call.nature_id, nature_label: call.nature_label }])
        .map((n, i) => (
          <span key={n.position} className="cnat-one">
            {i > 0 && ' + '}<strong>{n.nature_label}</strong>
            {n.position > 0 && (
              <button type="button" className="natx" disabled={busy}
                      aria-label={`Take ${n.nature_label} off`} title="Take it off"
                      onClick={() => {
                        if (!confirm(`Take ${n.nature_label} off this call? Its answers stay on the ticket.`)) return
                        change(() => api.incidentNatureRemove(call.id, n.position))
                      }}>✕</button>
            )}
          </span>
        ))}
      {!adding ? (
        <button type="button" className="natadd" disabled={busy}
                onClick={() => setAdding(true)}>+ Add another nature</button>
      ) : (
        <div className="cnat-pick">
          <input autoFocus value={filter} placeholder="Type to narrow — lockout, tire, or a code"
                 onChange={(e) => setFilter(e.target.value)}
                 onKeyDown={(e) => {
                   if (e.key === 'Escape') { e.preventDefault(); setAdding(false); setFilter('') }
                   if (e.key === 'Enter') {
                     e.preventDefault()
                     const first = offered[0]
                     if (first && filter.trim()) change(() => api.incidentNatureAdd(call.id, first.id))
                   }
                 }} />
          <div className="natlist" role="listbox" aria-label="Another nature">
            {offered.length === 0 ? (
              <div className="natnone">Nothing matches.</div>
            ) : offered.map((n) => (
              <div key={n.id} role="option" aria-selected={false} className="natopt"
                   onClick={() => { if (!busy) change(() => api.incidentNatureAdd(call.id, n.id)) }}>
                {n.code && <span className="natcode">{n.code}</span>}
                <span className="nm">{n.name}</span>
              </div>
            ))}
          </div>
          <button type="button" className="btn sm ghost" onClick={() => { setAdding(false); setFilter('') }}>
            Cancel
          </button>
        </div>
      )}
      {error && <p className="qalert" role="alert">{error}</p>}
    </div>
  )
}
